import { createCache, getValue } from '@glimmer/tracking/primitives/cache'
import { selectedDateTimesKey } from './keys'
import { rangeContains } from 'customer-flows/availability/ranges'
import { DateTime } from 'luxon'
import type { AvailabilityFamily, AvailabilityState, EngineArgs } from './types'
import type { Caches } from './caches'
import type { DatesWeekPair, Scopes } from './scopes'

/**
 * Builds the engine's state signals: `is(state, family?)` and `unknownWeekRanges`.
 *
 * Every read is pure and schedules no fetch.
 */
export function createSignals(
  caches: Caches,
  {
    keyScopes,
    currentDatesPairs
  }: { keyScopes: () => Scopes; currentDatesPairs: () => DatesWeekPair[] },
  args: () => EngineArgs,
  alive: () => boolean
) {
  const { datesCache, timesCache, usableDates, usableTimes } = caches

  /** The current range's week pairs that can be fetched. Without an activity id, none can. */
  function fetchablePairs(): DatesWeekPair[] {
    return currentDatesPairs().filter((pair) => !!pair.quantity.dims.variables.activityId)
  }

  /** Whether either of a week's entries has usable data. Both carry dates and their nested times. */
  function weekHasAnswer({ quantity, seed }: DatesWeekPair): boolean {
    return usableDates(datesCache.read(quantity.key)) || usableDates(datesCache.read(seed.key))
  }

  /** Whether a week's quantity-scoped entry has usable data. */
  function weekIsExact(pair: DatesWeekPair): boolean {
    return usableDates(datesCache.read(pair.quantity.key))
  }

  /**
   * The week ranges with no usable data from either entry.
   *
   * Weeks answer atomically, so this is the set of calendar tiles that should render a skeleton.
   */
  const unknownWeeks = createCache<string[]>(() =>
    fetchablePairs()
      .filter((pair) => !weekHasAnswer(pair))
      .map(({ quantity }) => quantity.weekRange)
  )

  /** @see unknownWeeks */
  function unknownWeekRanges(): string[] {
    return getValue(unknownWeeks) ?? []
  }

  /**
   * The calendar's answer quality across the current range's weeks.
   *
   * Reports the weakest week, so an unresolved week is never masked by resolved siblings.
   *
   * @returns undefined when there is nothing fetchable to describe
   */
  function datesState(): AvailabilityState | undefined {
    const pairs = fetchablePairs()
    if (!pairs.length) return undefined

    if (unknownWeekRanges().length > 0) return 'unknown'
    if (pairs.some((pair) => !weekIsExact(pair))) return 'approximate'

    return 'exact'
  }

  /**
   * The selected date's answer quality.
   *
   * `exact` once its own times entry answers, `approximate` while only the covering week's nested
   * `availabilityTimes` do. The times fetch can still reduce those slots, so the two differ.
   *
   * @returns undefined when no date is selected
   */
  const timesStateCache = createCache<AvailabilityState | undefined>(() => {
    const currentArgs = args()

    const scoped = selectedDateTimesKey(currentArgs)
    if (!scoped) return undefined
    if (!scoped.dims.variables.activityId) return undefined

    if (usableTimes(timesCache.read(scoped.key))) return 'exact'

    const selected = DateTime.fromISO(currentArgs.selectedDate!).startOf('day')
    const covering = fetchablePairs().find(({ quantity }) => rangeContains(quantity.weekRange, selected))

    // Scope has moved on, or the date is out of range — the times entry is the only source.
    if (!covering) return 'unknown'

    return weekHasAnswer(covering) ? 'approximate' : 'unknown'
  })

  /** @see timesStateCache */
  function timesState(): AvailabilityState | undefined {
    return getValue(timesStateCache)
  }

  /**
   * Whether a request is in flight for the anchored scope.
   *
   * Anchored rather than visible, because visible keys during a month-scrub are deliberately not
   * fetched.
   *
   * @param family limits the question to one family
   */
  function syncing(family?: AvailabilityFamily): boolean {
    const { anchored } = keyScopes()

    const dates = () => datesCache.pendingFor(anchored.dates).length > 0
    const times = () => timesCache.pendingFor(anchored.times).length > 0

    if (family === 'dates') return dates()
    if (family === 'times') return times()

    return dates() || times()
  }

  /**
   * Whether the engine is in `state`.
   *
   * With no `family`, reports the weakest answer across both, skipping either that has no question
   * to answer. All states read false before an activity resolves.
   *
   * @param state  an answer-quality state, or `syncing`
   * @param family limits the question to one family
   */
  function is(state: AvailabilityState | 'syncing', family?: AvailabilityFamily): boolean {
    if (!alive()) return false
    if (state === 'syncing') return syncing(family)

    if (family === 'dates') return datesState() === state
    if (family === 'times') return timesState() === state

    const states = [datesState(), timesState()].filter((s): s is AvailabilityState => !!s)
    if (!states.length) return false

    if (state === 'unknown') return states.includes('unknown')
    if (state === 'approximate') return !states.includes('unknown') && states.includes('approximate')

    return states.every((s) => s === 'exact')
  }

  return { is, unknownWeekRanges }
}
